import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { GoogleAdPreviewComponent } from '../../google-ad-preview/google-ad-preview.component';
import { GoogleAdsModalComponent } from './google-ads-modal/google-ads-modal.component';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { findGoogleAdsContent } from '../optional-setting/sms-delivery.mapper';

/**
 * Manages Google Ads section within a campaign editor: displays a preview, handles create/edit/delete flows via a modal, and emits changes to parent components.
 * @component
 * @selector app-google-ads
 * @example
 *   <app-google-ads [isExpanded]="true" [campaignData]="campaign" (toggle)="onToggle($event)" (done)="onDone()" (dataChange)="onDataChange($event)" (dataClear)="onDataClear()"></app-google-ads>
 * @input boolean isExpanded - Controls whether the collapsible Google Ads section is expanded or collapsed (default: true).
 * @input any campaignData - Campaign object used to initialize Google ad preview data (expects structure like campaignData.contents.google).
 * @input boolean isCompleted - Flag indicating whether the Google Ads step is completed (affects UI state).
 * @output EventEmitter<boolean> toggle - Emitted when the section expand/collapse state changes; payload is the new expanded state.
 * @output EventEmitter<void> done - Emitted when the user completes the Google Ads step (e.g., presses "Done").
 * @output EventEmitter<any> dataChange - Emitted when Google ad data is created or updated; payload contains normalized google ad fields (image, imageUrl, title, company).
 * @output EventEmitter<void> dataClear - Emitted when Google ad data is deleted/cleared.
 *
 * Key methods:
 *  - ngOnInit(): Initializes component and calls initializeAdData to populate adData from campaignData.
 *  - ngOnChanges(changes: SimpleChanges): Listens for changes to campaignData and re-initializes adData when it changes.
 *  - initializeAdData(): Private helper that extracts google ad content from campaignData.contents.google and sets adData with fields {image, imageUrl, title, company} or null if absent.
 *  - onGoogleAdsToggle(expanded: boolean): Emits the toggle event to notify parent of expand/collapse changes.
 *  - onGoogleAdsDone(): If no adData exists opens creation modal; otherwise emits done to signal completion.
 *  - onCreateGoogleAd() / onEditGoogleAds(): Sets edit/create mode and opens the modal for creating or editing an ad.
 *  - onDeleteGoogleAds(): Clears adData, emits dataClear, and logs the deletion action.
 *  - onCloseModal(): Closes the modal and resets edit mode flag.
 *  - onSaveAd(adData: any): Saves/updates adData, closes modal, shows a success toast via NotificationService when creating a new ad, and triggers onFormChange.
 *  - onFormChange(): Emits dataChange with normalized googleData (image, imageUrl, title, company) if adData exists.
 *
 * Important lifecycle hooks:
 *  - Implements OnInit to perform initial data setup.
 *  - Implements OnChanges to respond to updates of the campaignData input and reinitialize ad preview data.
 *
 * Notes:
 *  - adData is the local representation of the Google ad preview and drives UI state and emitted payloads.
 *  - The component opens a modal for create/edit flows (isModalOpen/isEditMode control modal state).
 *  - NotificationService is used to show a success toast when a new Google ad is created.
 */
@Component({
  selector: 'app-google-ads',
  imports: [CollapsibleSectionComponent, GoogleAdPreviewComponent, GoogleAdsModalComponent],
  templateUrl: './google-ads.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './google-ads.component.scss',
})
export class GoogleAdsComponent implements OnInit, OnChanges {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();
  @Output() dataClear = new EventEmitter<void>();

  isModalOpen = false;
  isEditMode = false;
  adData: any = null;

  constructor(private messageService: NotificationService) {}

  ngOnInit(): void {
    this.initializeAdData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaignData']) {
      this.initializeAdData();
    }
  }

  private initializeAdData(): void {
    const googleContent = findGoogleAdsContent(this.campaignData?.contents);
    if (googleContent && this.hasMeaningfulGoogleContent(googleContent)) {
      this.adData = {
        image: googleContent.g_ad_img || '',
        imageUrl: googleContent.g_ad_img || '',
        title: googleContent.g_ad_title || '',
        company: googleContent.g_ad_company || '',
      };
      return;
    }

    this.adData = null;
  }

  private hasMeaningfulGoogleContent(googleContent: {
    g_ad_img?: string;
    g_ad_title?: string;
    g_ad_company?: string;
  }): boolean {
    return Boolean(
      googleContent.g_ad_img?.trim() ||
      googleContent.g_ad_title?.trim() ||
      googleContent.g_ad_company?.trim()
    );
  }

  onGoogleAdsToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onGoogleAdsDone(): void {
    // Check if button text is "Create" or "Done" based on adData
    if (!this.adData) {
      // Button shows "Create" - open the create Google Ads modal
      this.onCreateGoogleAd();
    } else {
      // Button shows "Done" - emit the done event (keep old behavior)
      this.done.emit();
    }
  }

  onEditGoogleAds(): void {
    // Determine if we're in edit mode by checking if we have existing data
    this.isEditMode = Boolean(this.adData);
    this.isModalOpen = true;
  }

  onDeleteGoogleAds(): void {
    console.log('Delete Google Ads clicked');

    // Clear the ad data
    this.adData = null;

    // Emit clear event
    this.dataClear.emit();

    console.log('Google Ads data cleared and API updated');
  }

  onCreateGoogleAd(): void {
    // Determine if we're in edit mode by checking if we have existing data
    this.isEditMode = Boolean(this.adData);
    this.isModalOpen = true;
  }

  onCloseModal(): void {
    this.isModalOpen = false;
    // Reset edit mode flag when closing modal
    this.isEditMode = false;
  }

  /**
   * Save ad data, close the modal, show a success toast when creating (not editing), and emit a form change event.
   * @example
   * nSaveAd({ id: 123, title: 'Spring Sale', budget: 1000 })
   * undefined
   * @param {{any}} {{adData}} - Ad data object to save and process in the editor.
   * @returns {{void}} No return value.
   **/
  onSaveAd(adData: any): void {
    this.adData = adData;

    // Show success toast message only for create, not for edit (check before closing modal)
    const isCreating = !this.isEditMode;
    this.onCloseModal();
    this.isModalOpen = false;
    console.log('Ad saved:', adData);

    if (isCreating) {
      this.messageService.add({
        severity: 'success',
        summary: 'Google Ad created!',
        detail: 'A new Google ad campaign has been created successfully.',
      });
    }

    // Emit data change event
    this.onFormChange();
  }

  /**
   * Emit a simplified Google Ads payload (image, imageUrl, title, company) when the ad form changes.
   * @example
   * nFormChange()
   * undefined
   * @returns {void} Emits the simplified ad object via this.dataChange and returns nothing.
   **/
  onFormChange(): void {
    if (!this.adData) return;

    const googleData = {
      image: this.adData.image,
      imageUrl: this.adData.imageUrl,
      title: this.adData.title,
      company: this.adData.company,
    };

    this.dataChange.emit(googleData);
  }
}
